import { createAsyncThunk } from '@reduxjs/toolkit'
import { apiCall, useAppDispatch } from 'modules/core'
import { AttachmentEntity, MediaService } from 'openapi'
import { useState } from 'react'
import { ulid } from 'ulid'

const { mediaControllerUploadFileV1 } = MediaService

export const uploadFile = createAsyncThunk<AttachmentEntity, File>(
  'file/upload',
  async (file: File, { rejectWithValue }) => {
    const formData = {
      file,
      id: ulid(),
    }
    return await apiCall(mediaControllerUploadFileV1, rejectWithValue, formData)
  }
)

export interface UseFileUploadOptions {
  maxSize?: number
  allowedTypes?: string[]
  onSuccess?: (result: AttachmentEntity) => void
  onError?: (error: string) => void
}

export const useFileUpload = (options: UseFileUploadOptions = {}) => {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/'],
    onSuccess,
    onError,
  } = options

  const dispatch = useAppDispatch()
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleUpload = async (file: File) => {
    try {
      setIsUploading(true)
      setError(null)

      // Validate file type
      if (!allowedTypes.some(type => file.type.startsWith(type))) {
        throw new Error('Invalid file type')
      }

      // Validate file size
      if (file.size > maxSize) {
        throw new Error(`File size should be less than ${maxSize / 1024 / 1024}MB`)
      }

      // Upload the file
      const result = await dispatch(uploadFile(file)).unwrap()
      onSuccess?.(result)
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload file'
      setError(errorMessage)
      onError?.(errorMessage)
      throw err
    } finally {
      setIsUploading(false)
    }
  }

  return {
    handleUpload,
    isUploading,
    error,
  }
}

interface UseImageUploadOptions extends Omit<UseFileUploadOptions, 'allowedTypes'> {}

export const useImageUpload = (options: UseImageUploadOptions = {}) => {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
  } = options

  return useFileUpload({
    ...options,
    allowedTypes: ['image/'],
    maxSize,
  })
}
